import type { ComputedRef } from "vue";
import type { SeoPageMeta } from "../types/SeoPageMeta";

export function useSeoPack(seoDataComputed?: ComputedRef<SeoPageMeta>) {
  const route = useRoute();
  const { defaults } = useCurrentConfigKey();

  const domainPrepared = defaults.value.domain?.endsWith("/")
    ? defaults.value.domain.slice(0, -1)
    : defaults.value.domain;
  const hasTitleSuffix = computed<boolean>(
    () => seoDataComputed?.value?.hasTitleSuffix ?? true,
  );
  const hasOgTitleSuffix = computed<boolean>(
    () => seoDataComputed?.value?.hasOgTitleSuffix ?? false,
  );
  const hasTitlePrefix = computed<boolean>(
    () => seoDataComputed?.value?.hasTitlePrefix ?? false,
  );
  const hasOgTitlePrefix = computed<boolean>(
    () => seoDataComputed?.value?.hasOgTitlePrefix ?? false,
  );

  const images = computed(() => {
    let images =
      seoDataComputed?.value.ogImage || defaults.value.ogImage?.slice() || [];
    images = Array.isArray(images) ? (images as string[]) : [images];

    return (images as string[]).map((img) => ({
      property: "og:image",
      content: img.startsWith("http") ? img : domainPrepared + img,
    }));
  });

  const mergeData = computed(() => {
    return {
      title: `${(hasTitlePrefix.value && defaults.value.prefix) || ""}${seoDataComputed?.value.title ?? defaults?.value.title}${
        (hasTitleSuffix.value &&
          !hasTitlePrefix.value &&
          defaults.value.suffix) ||
        ""
      }`,
      meta: [
        {
          name: "title",
          content: formatTitle(
            seoDataComputed?.value.title ?? defaults?.value.title ?? "",
          ),
        },
        {
          name: "description",
          content: `${seoDataComputed?.value.description ?? (defaults?.value.description || "")}`,
        },
        {
          property: "og:title",
          content:
            hasOgTitleSuffix.value || hasOgTitlePrefix.value
              ? formatTitle(
                  seoDataComputed?.value.ogTitle ??
                    seoDataComputed?.value.title ??
                    defaults.value.title ??
                    "",
                )
              : (seoDataComputed?.value.ogTitle ??
                seoDataComputed?.value.title ??
                defaults.value.title),
        },
        {
          property: "og:description",
          content:
            seoDataComputed?.value.ogDescription ??
            seoDataComputed?.value.description ??
            defaults?.value.description,
        },
        {
          property: "og:type",
          content: seoDataComputed?.value.ogType ?? defaults.value.ogType,
        },
        { property: "og:url", content: domainPrepared + route.fullPath },
        { property: "og:locale", content: defaults.value.locale },
        { property: "og:site_name", content: defaults.value.ogSiteName },
        ...images.value,
      ],
    };
  });

  useHead(mergeData);

  function formatTitle(val: string): string {
    return `${defaults.value.prefix || ""}${val}${defaults.value.prefix ? "" : defaults.value.suffix}`;
  }

  return {};
}
